import {Component, OnDestroy, OnInit, AfterViewChecked, ChangeDetectorRef} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {forkJoin, Observable, of, Subject, Unsubscribable} from "rxjs";
import {RegistriesService} from "../../service";
import {DictionaryService, TableService, UserService, ValidatorService} from "@amlCore/services";
import {faSpinner} from "@fortawesome/free-solid-svg-icons/faSpinner";
import {Utils} from "@amlCore/utils";
import {FileSaverService} from "ngx-filesaver";
import {ActivatedRoute, Router} from '@angular/router';
import {ClientTypeEnum, RegistriesAccessEnum} from '@amlCore/enums';
import {ICustomSelectMapper} from '@amlCore/components';
import {IDictItemType} from "@amlCore/interfaces";
import {catchError, map, takeUntil} from "rxjs/operators";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons/faInfoCircle";

export interface ListTypeInfo {
  title: string;
  list?: Array<any>;
  totalElements?: number;
  totalSize?: number;
  id: string;
}

type QueryParamsType = {
  inboul: string;
  inn: string;
  spk: string;
  subjectType: ClientTypeEnum;
  birthDate: string;
  sort: string;
  dir?: string;
};

type ListTypeItemType = {
  type: string;
  downloadType: string;
  request: string;
  title: string;
  spk: boolean;
  accessProperty: string;
  excludedClientTypes?: ClientTypeEnum[];
  isDisabled?: boolean;
  disabledTooltip?: string;
  sort?: string;
  dir?: string;
};

@Component({
  selector: 'app-customer-check',
  templateUrl: './customer-check.component.html',
  styleUrls: ['./customer-check.component.scss']
})
export class CustomerCheckComponent implements OnInit, OnDestroy, AfterViewChecked {

  icons = {
    spinner: faSpinner,
    info: faInfoCircle
  };
  formCustomerCheckFilter: FormGroup;
  clientTypeEnum = ClientTypeEnum;
  regionMapper: ICustomSelectMapper;
  isAllEmptyResult = false;

  public subjectTypes = [] as IDictItemType[];

  customersValueChanges: Unsubscribable;
  private listsTypes: ListTypeItemType[] = [
    {
      type: 'checkListOfTerrorists',
      downloadType: 'TERROR',
      request: 'getTerroristList',
      title: 'Перечень экстремистов и террористов',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_TERROR,
      sort: 'idSubject'
    },
    {
      type: 'checkMvk',
      downloadType: 'MVK',
      request: 'getMvkList',
      title: 'Решения межведомственной комиссии',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_MVK,
      sort: 'idSubject'
    },
    {
      type: 'checkBounceRegister',
      downloadType: 'OTKAZ',
      request: 'getBounceRegisterList',
      title: 'Реестр 764-П',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_OTKAZ
    },
    {
      type: 'checkListOfInternalOrgs',
      downloadType: 'INTERNAL',
      request: 'getInternalOrgList',
      title: 'Внутренний перечень организации',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_INTERNALORG
    },
    {
      type: 'checkListRiskGroup',
      downloadType: 'RISK_GROUP',
      request: 'getRiskGroupSubjectList',
      title: 'Реестр 794-П',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_RISKGROUP,
      excludedClientTypes: [ClientTypeEnum.INDIVIDUAL_PERSON, ClientTypeEnum.PRIVATE_PRACTITIONER,
      ClientTypeEnum.FOREIGN_STRUCTURE]
    },
    {
      type: 'checkListConsolidated',
      downloadType: 'CONSOLIDATED',
      request: 'getConsolidatedList',
      title: 'Сводный перечень Совета Безопасности ООН',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_CONSOLIDATED,
      sort: 'idSubject',
      dir: 'ASC'
    },
    {
      type: 'checkListNelegal',
      downloadType: 'NELEGAL',
      request: 'getNelegalList',
      title: 'Список компаний с признаками нелегальной деятельности на финансовом рынке',
      spk: true,
      accessProperty: RegistriesAccessEnum.QREF_NELEGAL
    }
  ];

  availableListsTypes: ListTypeItemType[];
  lists: any = {};
  loaderArchive: string = undefined;
  loadingExcelFile: string = undefined;
  private filterValue: any;
  submitted = false;
  dictionary: { [name: string]: Array<any>; } = {};
  viewResulBlock: boolean;
  viewLoader: boolean;
  toggleIcon: boolean;
  hasNoResultCheckPassports = true;
  $destroy = new Subject();

  constructor(
    private regSrv: RegistriesService,
    private fSaver: FileSaverService,
    private formBuilder: FormBuilder,
    private dictSrv: DictionaryService,
    private activatedRoute: ActivatedRoute,
    protected tableSrv: TableService,
    private validationSrv: ValidatorService,
    private readonly userService: UserService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router
  ) {
    this.createLists();
    this.loadRemoteData(['fiasregion', 'okato']);
    this.initFormControl();
  }

  ngOnInit() {
    this.dictSrv.loadTypeClient().then(result => {
      this.subjectTypes = result.filter(item => !['0', '6'].includes(item.code));
    });
    this.tableSrv.sharedMenu().emit(this.activatedRoute.snapshot.data.pageTitle);
    this.customersValueChanges = this.f('type').valueChanges.subscribe(val => {
      this.setActiveClientType(val);
      this.viewResulBlock = false;
    });

    this.regionMapper = {
      toModel: this.regionMapperToModel.bind(this)
    } as ICustomSelectMapper;

    this.f('flIp.series').valueChanges.pipe(takeUntil(this.$destroy)).subscribe(
        (value: boolean) => this.initPassportCheckbox());
    this.f('flIp.number').valueChanges.pipe(takeUntil(this.$destroy)).subscribe(
        (value: boolean) => this.initPassportCheckbox());
    this.initListTypes();
  }

  initPassportCheckbox() : void{
    const passportType = this.availableListsTypes.find(t=>t.type === 'checkListOfInvalidPassports');
    if(passportType){
      passportType.isDisabled = this.isPassportCheckboxDisabled();
    }
  };

  initListTypes(): void{
    this.availableListsTypes = this.listsTypes.filter(t=>{
      return this.userService.checkAccess(t.accessProperty) && !t?.excludedClientTypes?.includes(this.f('type').value);
    });
    this.initPassportCheckbox();
  }

  isPassportCheckboxDisabled() : boolean{
    const series = this.f('flIp.series');
    const number = this.f('flIp.number');
    return !series.value || !number.value;
  }

  ngAfterViewChecked() {
    this._changeDetectorRef.detectChanges();
  }

  /*метод проверяющий. если проверку отключили (убрали галочку)
    но по данной проверки были ранее найдены результаты проверок, то
    данный предыдущий результат проверки очищаем*/
  private _resultPreviousCheckClear(formModel) {
    this.availableListsTypes.forEach( list => {
      if (!formModel[list.type] && typeof this.lists[list.type] === 'object' && this.lists[list.type].list) {
        this.lists[list.type].list = null;
      }
    });
  }

  /**
   * Маппер для региона
   */
  private regionMapperToModel(origin: any, isDictData: boolean): any {
    if (isDictData) {
      return origin.formalname + ' ' + origin.shortname;
    } else {
      return origin;
    }
  }

  ngOnDestroy(): void {
    this.customersValueChanges.unsubscribe();
    this.$destroy.next();
    this.$destroy.complete();
  }

  private createLists() {
    this.listsTypes.forEach((item) => {
      this.lists[item.type] = {
        title: item.title,
        id: item.type
      };
    });
  }

  loadRemoteData(types: Array<string>) {
    types.forEach(type => {
      this.dictionary[type] = [];
      this.dictSrv.getRemoteData(type).then(result => {
        this.dictionary[type] = result;
      });
    });
  }

  fillAddress(data) {
    this.formCustomerCheckFilter.get('ul').patchValue(
      {
        city: data.city,
        region: data.region,
        area: data.area,
        locality: data.locality,
        street: data.street,
        house: data.house,
        apartment: data.apartment,
        building: data.building
      }
    );
  }

  setActiveClientType(type: ClientTypeEnum) {
    this.formCustomerCheckFilter.get('ul').disable();
    this.formCustomerCheckFilter.get('flIp').disable();
    this.formCustomerCheckFilter.get('inboul').disable();
    if(!type) return;
    this.formCustomerCheckFilter.get(this.getType(type)).enable();
    this.initListTypes();
    // TODO Переделать через switch
    if (type === this.clientTypeEnum.LEGAL_ENTITY) { // Юридическое лицо
      this.formCustomerCheckFilter.patchValue({
        checkListOfTerrorists: true,
        checkMvk: true,
        checkBounceRegister: true,
        checkListOfInternalOrgs: true,
        checkListRiskGroup: true,
        checkListNelegal: true,
        checkListConsolidated: true
      });
    } else if (type === this.clientTypeEnum.INDIVIDUAL_PERSON) { // Физическое лицо
      this.formCustomerCheckFilter.patchValue({
        checkListOfTerrorists: true,
        checkMvk: true,
        checkBounceRegister: true,
        checkListOfInternalOrgs: true,
        checkListRiskGroup: false,
        checkListNelegal: false,
        checkListConsolidated: true
      });
    } else if (type === this.clientTypeEnum.INDIVIDUAL_ENTREPRENEUR) { // ИП
      this.formCustomerCheckFilter.patchValue({
        checkListOfTerrorists: true,
        checkMvk: true,
        checkBounceRegister: true,
        checkListOfInternalOrgs: true,
        checkListRiskGroup: true,
        checkListNelegal: false,
        checkListConsolidated: true
      });
    } else if (type === this.clientTypeEnum.PRIVATE_PRACTITIONER) { // Физическое лицо занимающееся частной практикой
      this.formCustomerCheckFilter.patchValue({
        checkListOfTerrorists: true,
        checkMvk: true,
        checkBounceRegister: true,
        checkListOfInternalOrgs: true,
        checkListRiskGroup: false,
        checkListNelegal: false,
        checkListConsolidated: true
      });
    } else if (type === this.clientTypeEnum.FOREIGN_STRUCTURE) { // Иностранная структура без образования ЮЛ
      this.formCustomerCheckFilter.patchValue({
        checkListOfTerrorists: true,
        checkMvk: true,
        checkBounceRegister: true,
        checkListOfInternalOrgs: true,
        checkListRiskGroup: false,
        checkListNelegal: false,
        checkListConsolidated: true
      });
    }
  }

  private getType(type: string) {
    switch (type) {
      case '1':
        return 'ul';
      case '2':
        return 'flIp';
      case '3':
        return 'flIp';
      case '4':
        return 'flIp';
      case '5':
        return 'inboul';
    }
  }

  f(field: string): AbstractControl {
    return this.formCustomerCheckFilter.get(field);
  }

  initFormControl() {
    const partTypeValidator: FlIpValidator = new FlIpValidator(this.validationSrv);
    this.filterValue = this.regSrv.savedFilterSPK;
    this.formCustomerCheckFilter = this.formBuilder.group({
      type: null,
      ul: this.formBuilder.group({
        name: null,
        inn: [null, this.validationSrv.getValidation({
            min: 5,
            max: 10,
            isINN: 'INN_KIO'
          })],
        ogrn: [null, this.validationSrv.getValidation({
            isOGRN: true
          })],
        region: null,
        area: null,
        city: null,
        locality: null,
        street: null,
        house: null,
        building: null,
        apartment: null
      }),
      flIp: this.formBuilder.group({
        sname: null,
        gname: null,
        pname: null,
        birthDate: null,
        inn: [null, this.validationSrv.getValidation({
          isINN: 'INN_12'
        })],
        ogrn: [null, this.validationSrv.getValidation({
          isOGRN: true
        })],
        series: null,
        number: null
      }, {validators: partTypeValidator.validator.bind(partTypeValidator)}),
      inboul: this.formBuilder.group({
        name: null,
        inboul: null,
        inn: [null, this.validationSrv.getValidation({
          min: 5,
          max: 10,
          isINN: 'INN_KIO'
        })],
      }),
      checkListOfTerrorists: false,
      checkMvk: false,
      checkBounceRegister: false,
      checkListOfInternalOrgs: false,
      checkListRiskGroup: false,
      checkListNelegal: false,
      checkListConsolidated: false
    });
    this.formCustomerCheckFilter.patchValue(this.filterValue.param);
    const typeVal = this.filterValue.param.type ? this.filterValue.param.type : '1';
    this.formCustomerCheckFilter.controls['type'].setValue(typeVal);
    this.setActiveClientType(typeVal);
    this.getSavedLists();
    if (this.filterValue.scroll.id) {
      setTimeout(() => this.scrollTo(this.filterValue.scroll.id), 0);
    }
  }

  /**
   * Метод проверки чекбоксов,
   * если все не нажаты, то возвращает true
   */
  checkInfoSource(): boolean {
    const infoSourseValue = [
      this.f('checkListOfTerrorists').value,
      this.f('checkMvk').value,
      this.f('checkBounceRegister').value,
      this.f('checkListOfInternalOrgs').value,
      this.f('checkListRiskGroup').value,
      this.f('checkListNelegal').value,
      this.f('checkListConsolidated').value
    ];
    return infoSourseValue.every(e => !e);
  }

  disableSubmitBtn(): boolean {
    if(!this.availableListsTypes.length)
      return false;
    // проверка, что заполнены оба поля "Серия документа" и "Номер документа",
    // если выбрана только проверка по "Переченю недействительных паспортов".
    // В других случаях проверка не происходит.
    const clientTypesWithPassportCheck = [this.clientTypeEnum.INDIVIDUAL_PERSON, this.clientTypeEnum.INDIVIDUAL_ENTREPRENEUR, this.clientTypeEnum.PRIVATE_PRACTITIONER];
    if (clientTypesWithPassportCheck.includes(this.f('type').value)
        && this.formCustomerCheckFilter.value['checkListOfInvalidPassports']) {

        let infoSourseValue = [
          this.f('checkListOfTerrorists').value,
          this.f('checkMvk').value,
          this.f('checkBounceRegister').value,
          this.f('checkListOfInternalOrgs').value,
          this.f('checkListNelegal').value,
          this.f('checkListConsolidated').value
        ];

      if (this.f('type').value === this.clientTypeEnum.INDIVIDUAL_ENTREPRENEUR) {
        infoSourseValue.push(this.f('checkListOfDebtorsOfFTS').value,
                             this.f('checkListRiskGroup').value)
      }
       if (infoSourseValue.every(value => value === false)) {
        const flIpValue = this.f('flIp').value;
        if (!flIpValue?.series || !flIpValue?.number) return false;
       }
    }

    const type = this.getType(this.formCustomerCheckFilter.value.type);
    const value = this.formCustomerCheckFilter.controls[type]?.value;
    if(!value)
      return false;
    const formValue = Utils.clone(value);
    Utils.deleteEmptyObj(formValue);
    return Object.keys(formValue).length > 0 && !this.checkInfoSource() && !this.formCustomerCheckFilter.invalid;
  }

  private getSavedLists() {
    // tslint:disable-next-line:forin
    if(Object.values(this.filterValue.lists).every(list => list == null || list['empty'])){
      return;
    }
    for (const key in this.filterValue.lists) {
      this.lists[key].list = this.filterValue.lists[key]?.content;
      this.lists[key].totalElements = this.filterValue.lists[key]?.totalElements;
      this.lists[key].totalSize = this.filterValue.lists[key]?.totalSize;
      this.updateViewResulBlock();
    }
  }

  private getParams(formValue: any) {
    const queryParams = this.removeEmptyValuesFromQuery(
      Object.assign({subjectType: formValue.type}, formValue[this.getType(formValue.type)])
    );

    return queryParams;
  }

  /*метод установки параметров для запроса*/
  private _getCheckedQueryParams(typeCheck: string, queryParams: QueryParamsType): QueryParamsType {
    return queryParams;
  }

  submitForm() {
    // проверям форму на наличие ошибок ФЛК
    this.submitted = true;
    if (this.formCustomerCheckFilter.invalid) {
      return;
    }
    const checkingListArr: Observable<any>[] = [];
    const formValue = this.formCustomerCheckFilter.value;
    const param = this.getParams(formValue) as QueryParamsType;
    this._resultPreviousCheckClear(formValue);
    this.regSrv.resetFilterSPK();
    this.viewResulBlock = false;
    this.viewLoader = true;
    const access = [];
    this.availableListsTypes.forEach((item) => {
      if (formValue[item.type] && !item?.isDisabled) {
        if (item.spk) {
          param.spk = String(true);
        } else {
          delete param.spk;
        }
        if (item.sort) {
          param.sort = item.sort;
        } else {
          delete param.sort;
        }
        if (item.dir) {
          param.dir = item.dir;
        } else {
          delete param.dir;
        }
        const queryParams = this._getCheckedQueryParams(item.type, Object.assign({}, param));
        checkingListArr.push(this.regSrv[item.request](queryParams));

        access.push(item);
      }
    });
    const neverFailReqs = checkingListArr.map(req => req.pipe(map(result => ({success: true, result})))
        .pipe(catchError(result => of(({success: false, result})))));
    forkJoin(neverFailReqs).subscribe(dataArr => {
      let isPassportsEmpty = true;
      dataArr.forEach((response: any, index) => {
        if(response.success){
          const data = response.result;
          // Заполнение списка для Проверки паспортов
          if (typeof data === 'boolean' && access[index].type === 'checkListOfInvalidPassports') {
            this.lists[access[index].type].list = data;
            this.viewResulBlock = true;
            isPassportsEmpty = !data;
          } else {
            this.lists[access[index].type].list = data.content || [];
            this.lists[access[index].type].totalElements = data.totalElements;
            this.lists[access[index].type].totalSize = data.totalSize;
          }
          this.updateViewResulBlock();
          this.saveFilterValue('lists', access[index].type, data);
        } else{
          console.error(response.result);
        }
      });
      this.viewLoader = false;
      this.isAllEmptyResult = isPassportsEmpty && Object.keys(this.lists).every(e => {
        const conditional = Array.isArray(this.lists[e].list);
        return conditional ? !this.lists[e].list.length : true;
      });
    }, (error) => {
      if (error instanceof Array) {
        error.forEach(errorItem => {
          console.error(errorItem.message);
        });
      } else {
        console.error(error);
      }
      this.viewLoader = false;
    });

    // tslint:disable-next-line:forin
    for (const key in formValue) {
      this.saveFilterValue('param', key, this.formCustomerCheckFilter.controls[key].value);
    }
  }

  clearForm(){
    this.regSrv.resetFilterSPK();
    this.viewResulBlock = false;
    this.formCustomerCheckFilter.reset();
    this.listsTypes.forEach((item, index) => {
      this.lists[item.type].list = null;
    });
  }

  saveFilterValue(groupKeyName: string, keyVal: string, newValue: any) {
    this.regSrv.savedFilterSPK = {groupName: groupKeyName, key: keyVal, value: newValue};
  }

  private removeEmptyValuesFromQuery<T>(query?: T) {
    return Object.entries(query).reduce((acc, [k, v]) => {
      if (v) {
        acc[k] = v.toString();
      }
      return acc;
    }, {} as Record<string, string>);
  }

  scrollTo(id: string) {
    document.getElementById(id).scrollIntoView();
  }

  private updateViewResulBlock() {
    this.viewResulBlock = true;
    const resultBlockLinks = document.getElementById('resultBlockLinks');
    if (resultBlockLinks) {
      this.saveFilterValue('scroll', 'resultBlockLinksHeight', resultBlockLinks.clientHeight);
    }
  }

  viewResulLink(id: string) {
    return this.formCustomerCheckFilter.controls[id].value;
  }

  viewResulLinkPointNone(id: string): boolean {
    return !(this.lists[id].list && this.lists[id].list.length);
  }

  getResulLinkText(id: string): string {
    let text: string;
    if (this.lists[id].list && this.lists[id].list.length) {
      text = `${this.lists[id].title}: Выявлено соответствий - ${this.lists[id].totalElements ?? this.lists[id].totalSize}.`;
    } else {
      text = `${this.lists[id].title}: Соответствий не выявлено.`;
    }
    return text;
  }

  getCheckInfoPassports(isTableInfo = false): string {
    const type = 'checkListOfInvalidPassports';
    let text = '';
    if (this.lists && this.lists[type]) {
      if (this.lists[type].list) {
        text = isTableInfo ? 'Указанный паспорт числится в списке недействительных российских паспортов.' : 'Выявлено соответствий - 1.'
        this.hasNoResultCheckPassports = false;
      } else {
        text = 'Соответствий не выявлено.';
        this.hasNoResultCheckPassports = true;
      }
    }
    return text;
  }

  navigateToPassportInfo(): void {
    if (!this.hasNoResultCheckPassports) {
      this._router.navigateByUrl('/cabinet/passportInfo');
    }
  }

  /**
   * Scrolling to id
   * @param id
   */
  scrollToId(id: string): void {
    document.getElementById(id).scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  getRegistriesListDownload(type?: string) {
    const result = [];
    if (type) {
      const filterType = this.listsTypes.filter(value => {
        return value.type === type;
      });
      result.push(filterType[0].downloadType);
    } else {
      this.listsTypes.forEach(value => {
          if (this.viewResulLink(value.type)) {
            result.push(value.downloadType);
          }
        }
      );
    }
    return result;
  }

  download(type?: string) {
    const formValue = this.formCustomerCheckFilter.value;
    const param: any = this.getParams(formValue);
    param.registries = this.getRegistriesListDownload(type);
    this.loaderArchive = type || 'all';
    this.regSrv.downloadSPK(param).subscribe((value) => {
        if (value.type === 4) {
          this.loaderArchive = undefined;
          this.fSaver.save(value.body, Utils.getFileNameFromContentDisposition(value));
        }
      }, error => {
        console.warn(error);
        this.loaderArchive = undefined;
      }
    );
  }

  /**
   * Экспорт в эксель
   * @param type - 'all'(все) или тип
   */
  exportExcelFile(type?: string) {
    const formValue = this.formCustomerCheckFilter.value;
    const param: any = this.getParams(formValue);
    param.registries = this.getRegistriesListDownload(type);
    this.loadingExcelFile = type || 'all';
    this.regSrv.exportSPKExcel(param).subscribe((value) => {
        if (value.type === 4) {
          this.loadingExcelFile = undefined;
          this.fSaver.save(value.body, Utils.getFileNameFromContentDisposition(value));
        }
      }, error => {
        console.warn(error);
        this.loadingExcelFile = undefined;
      }
    );
  }

  /**
   * Получение длины ОГРН в зависимости от типа
   */
  getOGRNLength(): number {
    if (this.f('type').value === '1') {
      return 13;
    } else {
      return 15;
    }
  }

  isFieldReq(fieldName: string): boolean{
    const control = this.f(fieldName);
    return this.validationSrv.isControlReq(control);
  }
}
/**
 * Валидация для группы flIp
 * Выставляем обязательные поле sname и inn
 */
export class FlIpValidator {

  constructor(protected validationSrv: ValidatorService){
  }
  validator(control: AbstractControl): null  {
    const sname = control.get('sname');         // фамилия
    const gname = control.get('gname');         // имя
    const pname = control.get('pname');         // отчество
    const birthDate = control.get('birthDate'); // дата рождения

    if (!sname || !gname || !birthDate) {
      return null;
    }

    // если заполнено имя или отчество или дата рождения то фамилия обязательна к заполнению
    if (gname.value || pname.value || birthDate.value) {
      sname.setValidators([Validators.required]);
    } else {
      sname.setValidators([]);
    }

    sname.updateValueAndValidity({onlySelf: true});
    return null;
  }
}
